import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import StarDivider from "@/components/StarDivider";
import { SITE } from "@/data/content";
import { DollarSign, Check } from "lucide-react";

const SUPPORTS = [
  "Candidate recruitment",
  "Training and mentorship",
  "Campaign outreach",
  "Communications",
  "Voter engagement",
  "Veteran-friendly leadership across Indiana",
];

export default function Donate() {
  return (
    <>
      <SEO
        title="Donate | Help Veterans Keep Serving Indiana"
        description="Donate to Veterans for Indiana to recruit, endorse, and elect veterans and military families ready to lead with integrity, accountability, and service."
        path="/donate"
      />

      <section className="relative bg-navy-deep text-white overflow-hidden">
        <div className="absolute inset-0 pattern-stars opacity-80" />
        <div className="container-x relative py-20 lg:py-28 max-w-4xl">
          <Reveal>
            <h1 className="h-display text-5xl sm:text-6xl text-white">Help Veterans Keep Serving Indiana</h1>
            <div className="mt-4"><StarDivider color="#5BA63B" inline /></div>
            <p className="mt-6 text-white/85 leading-relaxed max-w-3xl">
              Your support helps Veterans for Indiana recruit, endorse, and elect veterans and military family members who are ready to lead with integrity, accountability, and service.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={SITE.donateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary text-base"
                data-testid="donate-page-primary-cta"
              >
                <DollarSign size={18} /> Donate Now
              </a>
              <a
                href={SITE.donateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-ghost-light"
                data-testid="donate-page-actblue-cta"
              >
                Donate via ActBlue
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <h2 className="h-display text-3xl sm:text-4xl text-navy">Your contribution supports:</h2>
            <span className="mt-3 inline-block h-[3px] w-16 bg-green-brand" />
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {SUPPORTS.map((s) => (
                <li key={s} className="flex items-start gap-3 text-navy-deep/85">
                  <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-brand text-white">
                    <Check size={14} />
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={1}>
            <div className="rounded-2xl bg-cream p-8 ring-1 ring-gray-200">
              <h3 className="font-display tracking-widest text-navy uppercase text-lg">Suggested Contribution</h3>
              <span className="mt-2 inline-block h-[2px] w-10 bg-green-brand" />
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {["$25", "$50", "$100", "$250", "$500", "Other"].map((amt) => (
                  <a
                    key={amt}
                    href={SITE.donateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center rounded-md border-2 border-navy bg-white py-3 font-display tracking-widest uppercase text-navy hover:bg-green-brand hover:border-green-brand hover:text-white transition-colors"
                    data-testid={`donate-amount-${amt.replace(/\W/g, "")}`}
                  >
                    {amt}
                  </a>
                ))}
              </div>
              <a
                href={SITE.donateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary mt-6 w-full"
                data-testid="donate-page-actblue-bottom"
              >
                Continue on ActBlue
              </a>
              <p className="mt-3 text-xs text-navy-deep/60">
                Contributions to Veterans for Indiana, PAC are not tax-deductible.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-gray-soft py-10">
        <div className="container-x text-center">
          <p className="font-display tracking-[0.22em] text-sm uppercase text-navy">
            Paid for by Veterans for Indiana, PAC.
          </p>
        </div>
      </section>
    </>
  );
}
